import {
  ProfileButtonShape,
  ProfileHeaderStyle,
  ProfileLayout,
  ProfileTheme,
  type ProfileStylingDto,
} from '@up2stay/up2promo-api-client';
import { normalizeProfileAccentColor } from '@up2stay/up2promo-ui';

const PROFILE_LAYOUT_SET = new Set<string>(Object.values(ProfileLayout));
const PROFILE_THEME_SET = new Set<string>(Object.values(ProfileTheme));
const PROFILE_HEADER_STYLE_SET = new Set<string>(
  Object.values(ProfileHeaderStyle),
);
const PROFILE_BUTTON_SHAPE_SET = new Set<string>(
  Object.values(ProfileButtonShape),
);

export type ProfileHeaderImageValue = {
  id: string;
  path?: string;
} | null;

/** Styling fields shared by profile edit + template editors. */
export type ProfileStylingFormSlice = {
  layout: ProfileLayout;
  theme: ProfileTheme;
  headerStyle: ProfileHeaderStyle;
  headerImage: ProfileHeaderImageValue;
  buttonShape: ProfileButtonShape | null;
  accentColor: string | null;
};

export type ProfileStylingPayload = ProfileStylingDto;

export const PROFILE_STYLING_FORM_DEFAULTS: ProfileStylingFormSlice = {
  layout: ProfileLayout.CARDS,
  theme: ProfileTheme.AUTO,
  headerStyle: ProfileHeaderStyle.GRADIENT,
  headerImage: null,
  buttonShape: null,
  accentColor: null,
};

function asLayout(value: string | null | undefined): ProfileLayout {
  if (value != null && PROFILE_LAYOUT_SET.has(value)) {
    return value as ProfileLayout;
  }
  return ProfileLayout.CARDS;
}

function asTheme(value: string | null | undefined): ProfileTheme {
  if (value != null && PROFILE_THEME_SET.has(value)) {
    return value as ProfileTheme;
  }
  return ProfileTheme.AUTO;
}

function asHeaderStyle(value: string | null | undefined): ProfileHeaderStyle {
  if (value != null && PROFILE_HEADER_STYLE_SET.has(value)) {
    return value as ProfileHeaderStyle;
  }
  return ProfileHeaderStyle.GRADIENT;
}

function asButtonShape(
  value: string | null | undefined,
): ProfileButtonShape | null {
  if (value != null && PROFILE_BUTTON_SHAPE_SET.has(value)) {
    return value as ProfileButtonShape;
  }
  return null;
}

export function stylingToFormSlice(
  styling:
    | {
        layout?: string | null;
        theme?: string | null;
        headerStyle?: string | null;
        headerImage?: { id?: string; path?: string } | null;
        buttonShape?: string | null;
        accentColor?: string | null;
      }
    | null
    | undefined,
): ProfileStylingFormSlice {
  const headerImage =
    styling?.headerImage?.id != null && styling.headerImage.id !== ''
      ? {
          id: styling.headerImage.id,
          path: styling.headerImage.path,
        }
      : null;

  return {
    layout: asLayout(styling?.layout),
    theme: asTheme(styling?.theme),
    headerStyle: asHeaderStyle(styling?.headerStyle),
    headerImage,
    buttonShape: asButtonShape(styling?.buttonShape),
    accentColor: styling?.accentColor ?? null,
  };
}

export function isEphemeralFilePath(path: string | null | undefined): boolean {
  const trimmed = path?.trim() ?? '';
  return trimmed.startsWith('data:') || trimmed.startsWith('blob:');
}

/**
 * Builds the API styling payload from editor form values.
 * Accent is omitted when cleared so the public profile falls back to default blue.
 * Pass `keepEphemeralPaths` for live preview so crop data/blob URLs still render.
 */
export function buildProfileStylingPayload(
  form: ProfileStylingFormSlice,
  options?: { includeAccent?: boolean; keepEphemeralPaths?: boolean },
): ProfileStylingPayload {
  const includeAccent = options?.includeAccent !== false;
  const accentColor = includeAccent
    ? (normalizeProfileAccentColor(form.accentColor) ?? undefined)
    : undefined;

  const headerPath = form.headerImage?.path?.trim();
  const safeHeaderPath =
    headerPath &&
    (options?.keepEphemeralPaths || !isEphemeralFilePath(headerPath))
      ? headerPath
      : undefined;

  const payload: ProfileStylingPayload = {
    layout: form.layout,
    theme: form.theme,
    headerStyle: form.headerStyle,
    headerImage: form.headerImage
      ? {
          id: form.headerImage.id,
          ...(safeHeaderPath ? { path: safeHeaderPath } : {}),
        }
      : null,
  };

  if (form.buttonShape) {
    payload.buttonShape = form.buttonShape;
  }

  if (accentColor) {
    payload.accentColor = accentColor;
  }

  return payload;
}
